import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtLogCompact, tzLabel } from "@/lib/tz";
import { SEGMENTS } from "@/components/SendSmsDialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  MessageSquare, Search, Loader2, Send, Users, Trash2, Plus, Save, X, AlertTriangle, Megaphone,
} from "lucide-react";
import { toast } from "sonner";

const CRIMSON = "var(--brand)";
const TYPE_LABEL = { staff: "Staff", participant: "Participant", contact: "Contact", stakeholder: "Stakeholder", manual: "Manual" };

export default function Sms() {
  const { can } = useAuth();
  const [tab, setTab] = useState("compose");
  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="sms-page">
      <div className="flex items-center gap-2.5 mb-1">
        <MessageSquare className="h-5 w-5" style={{ color: CRIMSON }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">SMS</h1>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Text staff, participants and their contacts — every message is kept on the log. Sent from
        “Prydes”, so recipients can't reply.
      </p>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="sms-tabs">
          <TabsTrigger value="compose" data-testid="msg-tab-compose"><Send className="h-3.5 w-3.5 mr-1.5" />New SMS</TabsTrigger>
          <TabsTrigger value="log" data-testid="msg-tab-log"><MessageSquare className="h-3.5 w-3.5 mr-1.5" />Sent log</TabsTrigger>
          {can.has("sms.bulk_staff") && <TabsTrigger value="bulk" data-testid="msg-tab-bulk"><Megaphone className="h-3.5 w-3.5 mr-1.5" />Bulk to staff</TabsTrigger>}
          <TabsTrigger value="templates" data-testid="msg-tab-templates"><Save className="h-3.5 w-3.5 mr-1.5" />Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="compose" className="mt-4"><Compose /></TabsContent>
        <TabsContent value="log" className="mt-4"><SentLog /></TabsContent>
        {can.has("sms.bulk_staff") && <TabsContent value="bulk" className="mt-4"><BulkStaff /></TabsContent>}
        <TabsContent value="templates" className="mt-4"><Templates /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------------------------------------------ compose --- */
function Compose() {
  const [people, setPeople] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState([]);
  const [manual, setManual] = useState("");
  const [message, setMessage] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.smsRecipients().then(setPeople).catch(() => { setPeople([]); toast.error("Could not load the contact list."); });
    api.smsTemplates().then((r) => setTemplates(r.templates || [])).catch(() => {});
  }, []);

  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return [];
    return (people || []).filter((p) => p.name.toLowerCase().includes(n) || (p.mobile || "").includes(n)).slice(0, 40);
  }, [people, q]);

  const add = (p) => {
    if (!picked.some((x) => x.type === p.type && x.id === p.id)) setPicked([...picked, p]);
    setQ("");
  };
  const addManual = () => {
    const m = manual.trim();
    if (!m) return;
    setPicked([...picked, { type: "manual", id: null, name: m, mobile: m, textable: true }]);
    setManual("");
  };
  const drop = (i) => setPicked(picked.filter((_, idx) => idx !== i));

  const send = async () => {
    if (!picked.length) { toast.error("Add at least one recipient."); return; }
    if (!message.trim()) { toast.error("Type a message first."); return; }
    setSending(true);
    try {
      const res = await api.sendSms({
        recipients: picked.map((p) => ({ type: p.type, id: p.id, mobile: p.mobile, name: p.name })),
        message: message.trim(),
      });
      setResults(res);
      if (res.failed) toast.error(`${res.sent} sent, ${res.failed} failed.`);
      else { toast.success(`Sent to ${res.sent} recipient${res.sent === 1 ? "" : "s"}.`); setPicked([]); setMessage(""); setTemplateId(""); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the message.");
    } finally { setSending(false); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <Label className="text-xs">Who is this going to?</Label>
        <div className="relative mt-1.5">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-9 pl-8 text-sm"
            placeholder="Search staff, participants, contacts & stakeholders" data-testid="msg-recipient-search" />
        </div>
        {people === null && <div className="text-[11px] text-slate-400 mt-2">Loading contacts…</div>}
        {!!shown.length && (
          <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100" data-testid="msg-recipient-results">
            {shown.map((p) => (
              <button key={`${p.type}-${p.id}`} onClick={() => p.textable && add(p)} disabled={!p.textable}
                className="w-full text-left px-2.5 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                data-testid={`msg-recipient-${p.type}-${p.id}`}>
                <div className="text-xs font-medium text-slate-800">{p.name}</div>
                <div className="text-[10px] text-slate-400">
                  {TYPE_LABEL[p.type]}{p.detail ? ` · ${p.detail}` : ""} · {p.textable ? p.mobile : "no mobile on file"}
                </div>
                {p.guardian_only && <div className="text-[10px] font-semibold text-amber-700">Contact via guardian only</div>}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-1.5 mt-3">
          <Input value={manual} onChange={(e) => setManual(e.target.value)} className="h-9 text-sm"
            placeholder="Or type a mobile number (one-off)" data-testid="msg-manual-number"
            onKeyDown={(e) => e.key === "Enter" && addManual()} />
          <Button variant="outline" size="sm" className="h-9" onClick={addManual} data-testid="msg-manual-add">Add</Button>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="msg-picked">
          {picked.length === 0 && <div className="text-[11px] text-slate-400">No recipients yet.</div>}
          {picked.map((p, i) => (
            <span key={i} className={`inline-flex items-center gap-1 rounded-full border pl-2 pr-1 py-0.5 text-[11px] ${p.guardian_only ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-slate-100 border-slate-200 text-slate-700"}`}
              data-testid={`msg-picked-${p.type}-${p.id || p.mobile}`}>
              {p.guardian_only && <AlertTriangle className="h-3 w-3" />}
              {p.name}
              <button onClick={() => drop(i)} className="rounded-full hover:bg-slate-200 p-0.5" data-testid={`msg-picked-remove-${i}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
        {picked.some((p) => p.guardian_only) && (
          <p className="mt-2 text-[11px] text-amber-700" data-testid="msg-guardian-only-warning">
            {picked.filter((p) => p.guardian_only).map((p) => p.name).join(", ")} should be contacted through their legal guardian.
            Search for the guardian's name to text them instead.
          </p>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Message</Label>
          {templates.length > 0 && (
            <Select value={templateId} onValueChange={(v) => { setTemplateId(v); setMessage(templates.find((t) => t.id === v)?.body || ""); }}>
              <SelectTrigger className="h-7 w-44 text-[11px]" data-testid="msg-template-picker"><SelectValue placeholder="Use a template" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id} className="text-xs">{t.title}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        <Textarea value={message} onChange={(e) => { setMessage(e.target.value); if (!e.target.value) setTemplateId(""); }} rows={7} className="mt-1.5 text-sm"
          placeholder="Type your message… {first_name} is replaced with each person's first name."
          data-testid="msg-body" />
        <div className="text-[10px] text-slate-400 mt-1">
          {message.length} characters · {SEGMENTS(message)} SMS segment{SEGMENTS(message) === 1 ? "" : "s"} per recipient
        </div>
        <Button size="sm" className="mt-3 w-full" style={{ backgroundColor: CRIMSON }} onClick={send}
          disabled={sending || !picked.length || !message.trim()} data-testid="msg-send">
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
          Send to {picked.length} recipient{picked.length === 1 ? "" : "s"}
        </Button>

        {results && (
          <div className="mt-3 space-y-1" data-testid="msg-results">
            {results.results.map((r, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[11px]">
                <Badge className={`text-[9px] py-0 border ${r.status === "Sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>{r.status}</Badge>
                <span className="text-slate-700">{r.name}</span>
                {r.error && <span className="text-red-600">— {r.error}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------- sent log --- */
function SentLog() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");

  const load = () => api.smsLog({ q: q.trim() || undefined, kind: kind === "all" ? undefined : kind, limit: 300 })
    .then(setData).catch(() => { setData({ messages: [] }); toast.error("Could not load the message log."); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const rows = data?.messages || [];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            className="h-9 pl-8 text-sm" placeholder="Search name, number or message" data-testid="log-search" />
        </div>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-9 w-40 text-xs" data-testid="log-kind"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All messages</SelectItem>
            <SelectItem value="manual" className="text-xs">Sent by a person</SelectItem>
            <SelectItem value="bulk" className="text-xs">Bulk broadcasts</SelectItem>
            <SelectItem value="automated" className="text-xs">Automated</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-9" onClick={load} data-testid="log-refresh">Search</Button>
      </div>
      <div className="text-[10px] text-slate-400 mb-2">
        {data?.scope === "mine" ? "Showing messages you sent. Managers can see the full trail." : "Showing all messages sent from the system."}
        {" "}Times in {tzLabel()}.
      </div>
      {data === null && <div className="text-xs text-slate-400 py-6 text-center">Loading…</div>}
      {data && rows.length === 0 && <div className="text-xs text-slate-400 py-6 text-center" data-testid="log-empty">No messages yet.</div>}
      <div className="divide-y divide-slate-100">
        {rows.map((m) => (
          <div key={m.id} className="py-2" data-testid={`log-row-${m.id}`}>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge className={`text-[9px] py-0 border ${m.status === "Sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>{m.status}</Badge>
              <span className="text-xs font-medium text-slate-800">{m.to_name || m.to_mobile}</span>
              <span className="text-[10px] text-slate-400">{m.to_mobile}</span>
              {m.recipient_type && <Badge className="text-[9px] py-0 bg-slate-100 text-slate-600 border border-slate-200">{TYPE_LABEL[m.recipient_type] || m.recipient_type}</Badge>}
              {m.kind === "bulk" && <Badge className="text-[9px] py-0 bg-violet-50 text-violet-700 border border-violet-200">Bulk</Badge>}
              {m.kind === "automated" && <Badge className="text-[9px] py-0 bg-sky-50 text-sky-700 border border-sky-200">Automated</Badge>}
            </div>
            <div className="text-[11px] text-slate-700 mt-0.5 whitespace-pre-wrap">{m.body}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              {fmtLogCompact(m.at)} · {m.sent_by_name || m.source || "System"}
            </div>
            {m.error && <div className="text-[10px] text-red-600 mt-0.5">{m.error}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* --------------------------------------------------------- bulk staff --- */
function BulkStaff() {
  const [audience, setAudience] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sil, setSil] = useState("");
  const [props, setProps] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [pickedParts, setPickedParts] = useState([]);
  const [pickedSils, setPickedSils] = useState([]);
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState("");

  useEffect(() => { api.properties({ sil_type: undefined }).then(setProps).catch(() => setProps([])); api.participants().then(setParticipants).catch(() => setParticipants([])); }, []);

  const toggle = (setter) => (id) => { setter((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])); setPreview(null); };

  const body = () => ({ audience, message: message.trim(), date_from: dateFrom || null, date_to: dateTo || null, sil_property: sil || null, participants: pickedParts, sil_properties: pickedSils });

  const doPreview = async () => {
    setBusy(true);
    try { setPreview(await api.smsBulkPreview({ ...body(), message: message.trim() || "preview" })); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not work out that audience."); setPreview(null); }
    finally { setBusy(false); }
  };

  const doSend = async () => {
    setBusy(true);
    try {
      const res = await api.smsBulkSend({ ...body(), confirm_count: preview.count });
      toast.success(`Broadcast sent to ${res.sent} staff${res.failed ? `, ${res.failed} failed` : ""}.`);
      setConfirmOpen(false); setTyped(""); setMessage(""); setPreview(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send the broadcast."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="bulk-panel">
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2 text-[11px] text-amber-800 flex items-start gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
        Broadcasts go to many people at once and can't be recalled. Managers only.
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Who should get it?</Label>
          <Select value={audience} onValueChange={(v) => { setAudience(v); setPreview(null); }}>
            <SelectTrigger className="h-9 text-xs mt-1.5" data-testid="bulk-audience"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All active staff</SelectItem>
              <SelectItem value="rostered" className="text-xs">Staff rostered in a date range</SelectItem>
              <SelectItem value="sil" className="text-xs">Staff at a SIL house</SelectItem>
              <SelectItem value="preferred" className="text-xs">Preferred workers of participants / SIL houses</SelectItem>
            </SelectContent>
          </Select>

          {audience === "rostered" && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div><Label className="text-[10px]">From</Label><Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPreview(null); }} className="h-9 text-sm" data-testid="bulk-date-from" /></div>
              <div><Label className="text-[10px]">To</Label><Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPreview(null); }} className="h-9 text-sm" data-testid="bulk-date-to" /></div>
            </div>
          )}
          {audience === "sil" && (
            <Select value={sil} onValueChange={(v) => { setSil(v); setPreview(null); }}>
              <SelectTrigger className="h-9 text-xs mt-2" data-testid="bulk-sil"><SelectValue placeholder="Choose a house" /></SelectTrigger>
              <SelectContent>
                {props.map((p) => <SelectItem key={p.name} value={p.name} className="text-xs">{p.property_name || p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          {audience === "preferred" && (
            <div className="mt-2 space-y-2" data-testid="bulk-preferred">
              {[["Participants", participants, pickedParts, toggle(setPickedParts), (p) => p.display_name || p.name],
                ["SIL houses", props, pickedSils, toggle(setPickedSils), (p) => p.property_name || p.name]].map(([title, list, picked, flip, label]) => (
                <div key={title}>
                  <Label className="text-[10px]">{title} ({picked.length} selected)</Label>
                  <div className="max-h-36 overflow-y-auto rounded-md border border-slate-200 p-1.5 mt-1">
                    {list.map((p) => (
                      <label key={p.name} className="flex items-center gap-2 text-xs py-0.5 cursor-pointer">
                        <input type="checkbox" checked={picked.includes(p.name)} onChange={() => flip(p.name)} />
                        {label(p)}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <Button variant="outline" size="sm" className="h-8 mt-3 w-full" onClick={doPreview} disabled={busy} data-testid="bulk-preview">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Users className="h-3.5 w-3.5 mr-1.5" />}
            See who this reaches
          </Button>

          {preview && (
            <div className="mt-3 rounded-lg border border-slate-200 p-2.5" data-testid="bulk-preview-result">
              <div className="text-xs font-semibold text-slate-800">{preview.audience_label} — {preview.count} staff</div>
              <div className="max-h-40 overflow-y-auto mt-1 text-[11px] text-slate-600">
                {preview.recipients.map((r) => <div key={r.id}>{r.name} · {r.mobile}</div>)}
              </div>
              {!!preview.skipped_no_mobile.length && (
                <div className="mt-1.5 text-[10px] text-amber-700" data-testid="bulk-skipped">
                  Skipped (no mobile on file): {preview.skipped_no_mobile.map((r) => r.name).join(", ")}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <Label className="text-xs">Message</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} className="mt-1.5 text-sm"
            placeholder="Type your broadcast… {first_name} is replaced with each person's first name."
            data-testid="bulk-body" />
          <div className="text-[10px] text-slate-400 mt-1">{message.length} characters · {SEGMENTS(message)} segment{SEGMENTS(message) === 1 ? "" : "s"} each</div>
          <Button size="sm" className="mt-3 w-full" style={{ backgroundColor: CRIMSON }} data-testid="bulk-send"
            disabled={!preview?.count || !message.trim()} onClick={() => { setTyped(""); setConfirmOpen(true); }}>
            <Megaphone className="h-3.5 w-3.5 mr-1.5" />Send broadcast
          </Button>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(v) => !v && setConfirmOpen(false)}>
        <DialogContent data-testid="bulk-confirm-dialog">
          <DialogHeader><DialogTitle className="text-base">Send to {preview?.count} staff?</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-600 -mt-2">
            This goes to <b>{preview?.audience_label}</b> and can't be recalled. Type <b>{preview?.count}</b> to confirm.
          </p>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} className="h-9 text-sm"
            placeholder="Recipient count" data-testid="bulk-confirm-count" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: CRIMSON }} data-testid="bulk-confirm-send"
              disabled={busy || typed.trim() !== String(preview?.count)} onClick={doSend}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}Send now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------------------------------------------------- templates --- */
function Templates() {
  const [rows, setRows] = useState(null);
  const [merge, setMerge] = useState([]);
  const [edit, setEdit] = useState(null); // {id?, title, body}

  const load = () => api.smsTemplates().then((r) => { setRows(r.templates || []); setMerge(r.merge_fields || []); })
    .catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit.title.trim() || !edit.body.trim()) { toast.error("Give the template a name and a message."); return; }
    try {
      if (edit.id) await api.updateSmsTemplate(edit.id, { title: edit.title.trim(), body: edit.body.trim() });
      else await api.createSmsTemplate({ title: edit.title.trim(), body: edit.body.trim() });
      toast.success("Template saved.");
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the template."); }
  };

  const remove = async (t) => {
    try { await api.deleteSmsTemplate(t.id); toast.success("Template deleted."); load(); }
    catch { toast.error("Could not delete the template."); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="templates-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] text-slate-500">
          Merge fields: {merge.map((m) => m.token).join(", ") || "{first_name}"}
        </div>
        <Button size="sm" className="h-8 text-[11px]" style={{ backgroundColor: CRIMSON }}
          onClick={() => setEdit({ title: "", body: "" })} data-testid="template-new">
          <Plus className="h-3.5 w-3.5 mr-1" />New template
        </Button>
      </div>
      {rows === null && <div className="text-xs text-slate-400 py-6 text-center">Loading…</div>}
      {rows?.length === 0 && <div className="text-xs text-slate-400 py-6 text-center" data-testid="templates-empty">No templates yet.</div>}
      <div className="space-y-2">
        {(rows || []).map((t) => (
          <div key={t.id} className="rounded-lg border border-slate-200 px-3 py-2 flex items-start justify-between gap-2" data-testid={`template-row-${t.id}`}>
            <div className="min-w-0">
              <div className="text-xs font-semibold text-slate-800">{t.title}</div>
              <div className="text-[11px] text-slate-600 whitespace-pre-wrap">{t.body}</div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setEdit({ id: t.id, title: t.title, body: t.body })} data-testid={`template-edit-${t.id}`}>Edit</Button>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => remove(t)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent data-testid="template-dialog">
          <DialogHeader><DialogTitle className="text-base">{edit?.id ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
          <Input value={edit?.title || ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
            className="h-9 text-sm" placeholder="Name, e.g. Shift available tonight" data-testid="template-title" />
          <Textarea value={edit?.body || ""} onChange={(e) => setEdit({ ...edit, body: e.target.value })} rows={5}
            className="text-sm" placeholder="Hi {first_name}, …" data-testid="template-body" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEdit(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={save} data-testid="template-save">Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
